import type { IGame, InputAction, GameState } from "../../core/types";
import { clamp } from "../../core/Time";

type Block = {
  x: number;
  y: number;
  w: number;
};

export class StackGame implements IGame {
  private state: GameState = { status: "ready" };

  private blocks: Block[] = [];
  private current!: Block;

  private dir = 1;
  private speed = 220;
  private score = 0;
  private viewportW = 0;

  reset(): void {
    this.blocks = [];
    this.score = 0;
    this.speed = 220;
    this.dir = 1;

    this.blocks.push({
      x: 0,
      y: 0,
      w: 160
    });

    this.state = { status: "ready" };
  }

  getState(): GameState {
    if (this.state.status === "running") return { status: "running", score: this.score };
    if (this.state.status === "gameover") return this.state;
    return { status: "ready" };
  }

  onAction(action: InputAction): void {
    if (
      action.type === "tap" ||
      (action.type === "keydown" && action.key === " ")
    ) {
      if (this.state.status === "ready") {
        this.start();
        return;
      }

      if (this.state.status === "running") {
        this.place();
        return;
      }

      if (this.state.status === "gameover") {
        this.reset();
        this.start();
      }
    }
  }

  private start() {
    this.state = { status: "running", score: 0 };
    this.spawn();
  }

  private spawn() {
    const last = this.blocks[this.blocks.length - 1];
    const startX = this.dir === 1 ? -99999 : 99999; // on force l'arrivée depuis un bord
    this.current = {
    x: startX,
    y: last.y - 28,
    w: last.w
    };

  }

  private place() {
    const last = this.blocks[this.blocks.length - 1];

    const overlap =
      Math.min(this.current.x + this.current.w, last.x + last.w) -
      Math.max(this.current.x, last.x);

    if (overlap <= 4) {
      this.state = { status: "gameover", score: this.score, best: 0 };
      return;
    }

    this.current.w = overlap;
    this.current.x = Math.max(this.current.x, last.x);
    this.blocks.push({ ...this.current });

    this.score++;
    this.speed += 12;
    this.dir *= -1;

    this.spawn();
  }
    update(dt: number): void {
        if (this.state.status !== "running") return;

        // sécurité : si on n'a pas encore rendu une frame, on ne peut pas borner
        if (this.viewportW <= 0) return;

        this.current.x += this.dir * this.speed * dt;

        // current.x est une position "centrée" (offset par rapport au centre écran)
        const halfW = this.viewportW / 2;

        // limites pour que le bloc reste visible
        const minX = -halfW + this.current.w / 2;
        const maxX =  halfW - this.current.w / 2;

        if (this.current.x < minX) {
            this.current.x = minX;
            this.dir = 1;
        } else if (this.current.x > maxX) {
            this.current.x = maxX;
            this.dir = -1;
        }
    }


  render(ctx: CanvasRenderingContext2D): void {
    const w = ctx.canvas.width;
    const h = ctx.canvas.height;

    this.viewportW = w;

    ctx.clearRect(0, 0, w, h);
    ctx.fillStyle = "rgba(255,255,255,0.03)";
    ctx.fillRect(0, 0, w, h);

    const centerX = w / 2;
    const baseY = h * 0.75;

    // blocs posés
    for (let i = 0; i < this.blocks.length; i++) {
      const b = this.blocks[i];
      ctx.fillStyle = "rgba(34,197,94,0.85)";
      ctx.fillRect(
        centerX + b.x - b.w / 2,
        baseY + b.y,
        b.w,
        22
      );
    }

    // bloc courant
    if (this.state.status === "running") {
      ctx.fillStyle = "rgba(59,130,246,0.85)";
      ctx.fillRect(
        centerX + this.current.x - this.current.w / 2,
        baseY + this.current.y,
        this.current.w,
        22
      );
    }

    ctx.fillStyle = "rgba(229,231,235,0.8)";
    ctx.font = "14px system-ui";
    ctx.fillText(
      this.state.status === "ready"
        ? "Espace / clic pour démarrer"
        : "Espace pour poser",
      24,
      h - 24
    );
  }
}
